import type { ItemFieldRow } from './item-field-reader';
import { createStatusLayerReadContext } from './status-row-construction';
import type { StatusOriginLayerDependencies } from './status-row-construction';
import { phpAdd } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';
import { isPhpNumeric } from './php-decimal-values';
import { phpJsonTruthy } from './php-json-values';
import { putIntegerCompilePath } from './integer-compute-slots';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown,key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]));
}
export type InventoryGrossNumberSemantics = 'json' | 'compiled-economy';
const boundedInteger = (token: string): bigint | null => {
    if (!/^[+-]?\d+$/.test(token)) return null;
    const value = BigInt(token);
    return value >= -9223372036854775808n && value <= 9223372036854775807n ? value : null;
};
function operand(value: unknown,semantics: InventoryGrossNumberSemantics): PhpArithmetic {
    if (value == null || value === false) return 0n;
    if (value === true) return 1n;
    if (typeof value === 'number') return semantics === 'compiled-economy' || !Number.isFinite(value)
        ? value : boundedInteger(JSON.stringify(value)) ?? value;
    if (typeof value !== 'string') throw new TypeError('Unsupported gross-weight arithmetic operand');
    const token = value.match(/^[\t\n\r\v\f ]*([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)/i)?.[1];
    if (token === undefined) throw new TypeError('Unsupported gross-weight arithmetic operand');
    return boundedInteger(token) ?? Number(token);
}
function sameNumeric(left: PhpArithmetic,right: PhpArithmetic): boolean {
    return typeof left === 'bigint' && typeof right === 'bigint' ? left === right : Number(left) === Number(right);
}
/** Exact source gross-weight block. 'json' models the typed JSON replay boundary;
 * a fresh constructor must select 'compiled-economy': its total/held numeric slots
 * were produced as PHP doubles by the earlier economy/container stages. Strings
 * and malformed inputs retain their actual source types in either mode.
 */
export function compileInventoryGrossWeight(input: ItemFieldRow,hasLayer: (row: ItemFieldRow,key: string) => boolean,
    semantics: InventoryGrossNumberSemantics = 'json'): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Gross weight compile requires a PHP array');
    if (semantics !== 'json' && semantics !== 'compiled-economy') throw new TypeError('Unknown gross numeric provenance');
    // Clone BEFORE adding the union result: a PHP union can create a non-list
    // numeric-key array. A later generic JSON-array canonicalizer would erase it.
    const row = clone(input) as ItemFieldRow,weight = own(own(row,'computed'),'weight');
    const total = own(weight,'total') ?? 0;
    if (hasLayer(row,'carrying_capacity_fixed_weight')) return row;
    const held = own(weight,'held') ?? 0;
    let gross: unknown,changed: boolean;
    if (arrayLike(total) || arrayLike(held)) {
        if (!arrayLike(total) || !arrayLike(held)) throw new TypeError('Array gross arithmetic requires two PHP arrays');
        const entries = new Map(Object.entries(total));
        for (const [key,value] of Object.entries(held)) if (!entries.has(key)) entries.set(key,value);
        changed = entries.size !== Object.keys(total).length;
        const list = [...entries];
        gross = list.every(([key],i)=>key===String(i)) ? list.map(([,value])=>value) : Object.fromEntries(list);
    } else {
        const left = operand(total,semantics),sum = phpAdd(left,operand(held,semantics));
        gross = Number(sum);
        if (typeof total === 'boolean') changed = (typeof sum === 'bigint' ? sum !== 0n : phpJsonTruthy(sum)) !== total;
        // A leading-numeric, nonnumeric-string operand cannot equal the numeric
        // sum under PHP 8's numeric-to-nonnumeric-string comparison.
        else if (typeof total === 'string' && !isPhpNumeric(total)) changed = true;
        else changed = !sameNumeric(sum,left);
    }
    if (changed) putIntegerCompilePath(row as Record<string,unknown>,['computed','weight','gross'],gross);
    return row;
}
export function createInventoryGrossWeightCompiler(deps: StatusOriginLayerDependencies):
    (row: ItemFieldRow,semantics?: InventoryGrossNumberSemantics) => ItemFieldRow {
    const context = createStatusLayerReadContext(deps);
    return (row,semantics='json')=>compileInventoryGrossWeight(row,context.hasLayer,semantics);
}
